import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-comparison",
  title: "Bible verses about comparison and envy: 10 passages",
  h1: "Bible verses about comparison",
  description:
    "Ten passages on comparison, envy and contentment, with notes on what each says — for anyone who has closed an app feeling worse about a life that had not changed.",
  targets: [
    "bible verses about comparison",
    "scripture about envy and jealousy",
    "bible verses about contentment social media",
  ],
  published: "2026-08-26",
  standfirst:
    "Nobody sets out to compare. It is what a feed does to you while you are looking at something else, and scripture is unusually clear-eyed about the damage.",
  takeaways: [
    "2 Corinthians 10:12 names the mechanism precisely: people measuring themselves by themselves and comparing themselves with themselves, and calls it a failure of understanding rather than a sin of the heart.",
    "Galatians 6:4 gives the practical alternative — examine your own work — which is the only instruction in the list that can actually be carried out.",
    "Proverbs 14:30 locates envy in the body, calling it rot in the bones, which is a fair description of how it feels a week later.",
    "Contentment is described in Philippians 4:11 as something Paul learned, not something he had — which means it is available and takes time.",
  ],
  intro: [
    {
      type: "p",
      text: "Comparison is not usually a decision. It is a side effect: you open something to see one thing, and you close it twenty minutes later with a slightly worse opinion of a life that has not changed in the meantime. The particular cruelty of a feed is that it compares your interior — which you know in full, including this morning — with other people's exteriors, edited and selected. No one would accept that as a fair test if it were proposed out loud.",
    },
    {
      type: "p",
      text: "Scripture treats this less as a feeling to be repented of and more as a mistake in reasoning, which is a considerable relief. The most direct passage below calls comparison a failure of understanding. The one that follows it hands over a different measure entirely, and it is the only genuinely actionable instruction in the collection.",
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain.",
    },
  ],
  verses: [
    {
      verse: {
        text: "But they themselves, measuring themselves by themselves, and comparing themselves with themselves, are without understanding.",
        ref: "2 Corinthians 10:12",
      },
      note: "The sentence is deliberately circular because the practice is. Paul's charge is not that comparison is unkind but that it is unintelligent: the measure being used has no fixed point in it, so the result carries no information. Anyone who has felt successful and then felt like a failure within ninety seconds of opening an app has run the experiment.",
    },
    {
      verse: {
        text: "But let each man examine his own work, and then he will have reason to boast in himself, and not in someone else.",
        ref: "Galatians 6:4",
      },
      note: "The alternative, and the reason this list is not simply ten ways of saying stop it. The instruction replaces one measurement with another rather than removing measurement — you are still assessing something, just your own work rather than the gap between your life and a stranger's summary of theirs.",
    },
    {
      verse: {
        text: "The life of the body is a heart at peace, but envy rots the bones.",
        ref: "Proverbs 14:30",
      },
      note: "A physical image, and an accurate one. Envy does not announce itself as an event; it works slowly and structurally, and its effects show up as a general dissatisfaction whose cause is no longer traceable to any particular evening.",
    },
    {
      verse: {
        text: "Beware! Keep yourselves from covetousness, for a man's life doesn't consist of the abundance of the things which he possesses.",
        ref: "Luke 12:15",
      },
      note: "The warning is about the definition of a life, not about the goods. The parable that follows features a man whose plans are entirely reasonable and entirely about storage. What indicts him is that the account of his life contains nobody else in it.",
    },
    {
      verse: {
        text: "Not that I speak because of lack, for I have learned in whatever state I am, to be content in it.",
        ref: "Philippians 4:11",
      },
      note: "Learned is the operative word, written by someone with an unusually wide range of circumstances to have learned it across. Contentment is presented as a skill acquired over time rather than a temperament some people are issued with, which makes it available to people who currently have none.",
    },
    {
      verse: {
        text: "But godliness with contentment is great gain.",
        ref: "1 Timothy 6:6",
      },
      note: "Written into a passage about people who had begun to treat faith as a route to profit. The pairing is the point: godliness without contentment turns into ambition wearing better clothes, which is a failure mode that looks like devotion from the outside.",
    },
    {
      verse: {
        text: "Be free from the love of money, content with such things as you have, for he has said, “I will in no way leave you, neither will I in any way forsake you.”",
        ref: "Hebrews 13:5",
      },
      note: "The reason attached to the command is not that money is bad but that you are not alone. Comparison usually runs on a fear of being left behind, and the answer offered here goes to that fear rather than to the arithmetic.",
    },
    {
      verse: {
        text: "For where jealousy and selfish ambition are, there is confusion and every evil deed.",
        ref: "James 3:16",
      },
      note: "Confusion is the striking word — the first casualty of envy is clarity about your own situation. James is describing a community, and the observation holds there too: groups in which people are quietly measuring each other lose the ability to think straight together.",
    },
    {
      verse: {
        text: "I will give thanks to you, for I am fearfully and wonderfully made.",
        ref: "Psalm 139:14",
      },
      note: "Frequently reduced to a slogan about self-esteem, which weakens it. In context it is part of a psalm about being known completely — including the parts the writer would rather not have on record. That is what makes it a genuine answer to comparison: it is a claim about being seen, not about being impressive.",
    },
    {
      verse: {
        text: "You shall not covet your neighbor's house … nor anything that is your neighbor's.",
        ref: "Exodus 20:17",
      },
      note: "The only commandment that legislates an interior state, and the last one, which some readers take as a summary: every other prohibition begins here. Worth noticing that the objects listed are all a neighbour's — that is, someone close enough to be known. The comparison that damages people is rarely with the truly distant.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "what-actually-helps",
      text: "What actually reduces it",
    },
    {
      type: "p",
      text: "Reading these will not stop it, and it is worth being honest about that. Comparison is not sustained by a lack of information; it is sustained by exposure, and the exposure is now continuous, personalised and designed by people whose job is to keep it that way. A passage read on Sunday is competing with several hundred impressions a day.",
    },
    {
      type: "p",
      text: "Two things move it. The first is Galatians 6:4 taken literally: look at your own work, weekly, in writing, so there is a real assessment for the vague one to compete with. The second is reducing the exposure, which is a practical problem with practical solutions — {a-social-media-fast} is the version most people can actually complete, and the notes there on what to do with the gap matter more than the fast itself.",
      links: { "a-social-media-fast": "/blog/social-media-fast-guide" },
    },
    {
      type: "h2",
      id: "a-prayer-about-comparison",
      text: "A prayer for after the feed",
    },
    {
      type: "p",
      text: "Lord, I have been measuring myself against people I do not know, using a ruler with no fixed point on it. Show me my own work today, and let that be enough to look at. Where I want what somebody else has, teach me the contentment Paul had to learn. Amen.",
    },
    {
      type: "note",
      text: "The test for whether an account is doing you harm is not whether the person is admirable. It is how you feel about your own week ten minutes after closing it.",
    },
  ],
  faq: [
    {
      q: "Is comparison always wrong?",
      a: "No, and the passages here do not say so. Scripture regularly holds up examples to imitate, and Galatians 6:4 replaces comparison with self-examination rather than abolishing assessment. What 2 Corinthians 10:12 condemns is narrower: measuring yourself by other people, which produces a number that means nothing.",
    },
    {
      q: "What does the Bible say about social media?",
      a: "Nothing directly, and any page claiming otherwise is stretching. What it does address is envy, covetousness, contentment and the use of time, which between them cover most of what people are actually asking about. Applying an ancient text honestly means saying which part is application and which part is text.",
    },
    {
      q: "How do I stop feeling envious of people at church?",
      a: "James 3:16 suggests starting with the effect rather than the feeling: envy in a community produces confusion, so the practical step is usually a conversation rather than an inward struggle. Proximity is what makes it sharp, and proximity is also what can undo it.",
    },
  ],
  related: [
    "social-media-fast-guide",
    "digital-sabbath",
    "screen-time-limits-dont-work",
  ],
  relatedTopics: ["bible-verses-about-time", "bible-verses-about-self-control"],
};
